"use client";

import { useEffect, useState } from "react";
import Link from "@/components/no-prefetch-link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link2 } from "lucide-react";
import { secureGet } from "@/lib/security/fetch-client";
import { encodeId } from "@/lib/id-encoder";

interface RelatedTopic {
  id: number;
  name: string;
  reason: string;
  relevance?: number;
}

interface RelatedTopicsProps {
  topicId: number;
  topicName: string;
}

export function RelatedTopics({ topicId }: RelatedTopicsProps) {
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState<RelatedTopic[]>([]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    secureGet<{ related: RelatedTopic[] }>(`/api/topic/${encodeId(topicId)}/related`)
      .then((data) => {
        if (!cancelled) setTopics(data.related || []);
      })
      .catch((error) => {
        console.error("获取相关词条失败:", error);
        if (!cancelled) setTopics([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [topicId]);

  if (!loading && topics.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-5 w-5 text-blue-500" />
          相关词条
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {topics.map((topic) => (
              <Link
                key={topic.id}
                href={`/topic/${encodeId(topic.id)}`}
                prefetch={false}
                className="block rounded-lg border bg-background p-3 transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{topic.name}</div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {topic.reason}
                    </div>
                  </div>
                  {typeof topic.relevance === "number" && (
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {Math.round(topic.relevance * 100)}%
                    </Badge>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
